// frontend/src/layouts/MainLayout.tsx
import React, { useState, useEffect } from "react";
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  IconButton, 
  Toolbar, 
  Typography,
  Tooltip,
  Container,
  Avatar,
  CircularProgress,
  Backdrop
} from "@mui/material";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useAuth } from "../context/AuthContext";
import BreadcrumbNavigation from "../components/BreadcrumbNavigation";
import axios from "../utils/axiosConfig";
import ClubLogo from '../components/ClubLogo';
import Header from '../components/Header';

import { handleApiError } from "../utils/apiErrorHandler";
import Footer from "../components/Footer";

import logger from '../utils/logger';

const MainLayout: React.FC = () => {
  // ← NEU: isLoginInProgress hinzugefügt für besseres Session-Management
  const { userRole, logout, loading, isInitialized, isLoginInProgress } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // User/Club data states
  const [clubName, setClubName] = useState<string>("");
  const [clubId, setClubId] = useState<string>("");
  const [hasLogo, setHasLogo] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>("");
  const [pageData, setPageData] = useState<any>({});
  
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  
  // Fetch club info for the logged-in user
  useEffect(() => {
    const fetchUserInfo = async () => {
      // Warten bis Auth initialisiert ist UND Login abgeschlossen ist
      if (!isInitialized) {
        logger.info('⏳ MainLayout: Auth not initialized yet...');
        return;
      }
      
      // ← NEU: Nicht laden während Login-Prozess läuft
      if (isLoginInProgress) {
        logger.info('⏳ MainLayout: Login in progress, waiting...');
        return;
      }
      
      if (loading) {
        logger.info('⏳ MainLayout: Auth still loading...');
        return;
      }
      
      if (!userRole) {
        logger.info('⏳ MainLayout: No userRole yet...');
        return;
      }
      
      if (userDataLoaded) {
        logger.info('✅ MainLayout: User data already loaded');
        return;
      }

      setUserDataLoading(true);
      
      try {
        logger.info('🚀 MainLayout: Fetching user info...');
        
        // Erweiterte Retry-Logic mit exponential backoff
        let retryCount = 0;
        const maxRetries = 1;
        let response: any = undefined;
        
        while (retryCount < maxRetries) {
          try {
            response = await axios.get('/api/users/me', {
              timeout: 10000
            });
            break; // Erfolgreich, schleife verlassen
          } catch (error: any) {
            retryCount++;
            const isLastRetry = retryCount >= maxRetries;
            
            logger.warn(`⚠️ MainLayout: User info retry ${retryCount}/${maxRetries}`, { 
              error: error.response?.status,
              isLastRetry 
            });
            
            // Bei 404 Fehlern länger warten (Session etabliert sich noch)
            if (error.response?.status === 404 && !isLastRetry) {
              const waitTime = Math.min(2000 * retryCount, 8000); // Max 8 Sekunden
              logger.info(`⏳ MainLayout: 404 error, waiting ${waitTime}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, waitTime));
              continue;
            }
            
            if (isLastRetry) {
              throw error;
            }
            
            // Standard exponential backoff für andere Fehler
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }
        
        logger.info('✅ MainLayout: User info loaded successfully');

        if (response && response.data) {
          // Benutzernamen setzen
          if (response.data.name) {
            setUserName(response.data.name);
          }
          
          if (response.data.club) {
            if (response.data.club.name) {
              setClubName(response.data.club.name);
            }
            if (response.data.club._id) {
              setClubId(response.data.club._id);
            }
            if (response.data.club.hasLogo) {
              setHasLogo(response.data.club.hasLogo);
            }
          }
          
          setUserDataLoaded(true);
        }
      } catch (error: any) {
        logger.error('❌ MainLayout: User info failed after retries', { 
          error: error.response?.status,
          errorMessage: error.message 
        });
        
        // Bei 404-Fehlern nach Login weniger aggressiv reagieren
        if (error.response?.status === 404) {
          logger.warn('⚠️ MainLayout: User not found - may be a timing issue after login');
          // Versuche es in 5 Sekunden nochmal
          setTimeout(() => {
            if (userRole && !userDataLoaded) {
              logger.info('🔄 MainLayout: Retrying user info after 404...');
              setUserDataLoaded(false); // Reset um erneuten Versuch zu ermöglichen
            }
          }, 5000);
          return;
        }
        
        // Bei 401/403 nicht als Fehler behandeln (Auth-Problem)
        if (error.response?.status === 401 || error.response?.status === 403) {
          logger.info('🔄 MainLayout: Auth error, will retry when auth is fixed');
          return;
        }
        
        handleApiError(error, "Fehler beim Laden der Benutzerdaten.");
      } finally {
        setUserDataLoading(false);
      }
    };
    
    fetchUserInfo();
  }, [loading, userRole, isInitialized, userDataLoaded, isLoginInProgress]);
  
  function isValidMongoId(anId: string) {
    return anId && /^[0-9a-fA-F]{24}$/.test(anId);
  }

  // Fetch additional data needed for breadcrumb labels based on current path
  useEffect(() => {
    const fetchPageData = async () => {
      // Auch hier auf Login-Completion warten
      if (!isInitialized || loading || userDataLoading || isLoginInProgress) {
        return;
      }
      
      if (!userRole) {
        return;
      }
      
      try {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        
        // If we're on a competition page, fetch the competition name
        if (pathSegments.includes('competition') && pathSegments.length > 1) {
          const competitionId = pathSegments[pathSegments.indexOf('competition') + 1];
          if (isValidMongoId(competitionId)) {
            try {
              const response = await axios.get(`/api/competitions/${competitionId}`, {
                timeout: 8000
              });
              setPageData({
                ...pageData,
                competitionName: response.data.name,
                competitionId: competitionId
              });
            } catch (error: any) {
              logger.warn('⚠️ MainLayout: Could not load competition data', { 
                competitionId, 
                error: error.response?.status 
              });
            }
          }
        }
      } catch (error) {
        logger.error('❌ MainLayout: Page data loading failed', { error });
      }
    };
    
    fetchPageData();
  }, [location.pathname, isInitialized, loading, userRole, userDataLoading, isLoginInProgress]);

  // Reset user data when user role changes (logout/login)
  useEffect(() => {
    if (!userRole) {
      setUserName("");
      setClubName("");
      setClubId("");
      setHasLogo(false);
      setUserDataLoaded(false);
      setPageData({});
    }
  }, [userRole]);

  // ← NEU: Show loading screen during initial auth check or login process
  if (!isInitialized || (loading && !userRole) || isLoginInProgress) {
    return (
      <Backdrop 
        open={true} 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">
          {isLoginInProgress ? 'Anmeldung läuft...' : 'Anmeldung wird überprüft...'}
        </Typography>
      </Backdrop>
    );
  }

  // Main content
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <CssBaseline />

      {/* ← NEU: Header mit SessionStatus-Support */}
      <Header 
        clubName={clubName} 
        clubId={clubId} 
        hasLogo={hasLogo}
        userName={userName}
        showSessionStatus={true} // ← NEU: Session-Status aktivieren
      />
      
      {/* Main content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          width: '100%',
          mt: '64px'
        }}
      >
        {/* Breadcrumb Navigation - only show if not on dashboard */}
        {location.pathname !== "/dashboard" && (
          <Box sx={{ px: 3, pt: 3 }}>
            <BreadcrumbNavigation 
              useAutoConfig={true} 
              data={pageData}
            />
          </Box>
        )}
        
        {/* Page Content */}
        <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
          {/* Show loading indicator if user data is still loading, aber nur wenn nicht gerade Login läuft */}
          {userDataLoading && !isLoginInProgress && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Benutzerdaten werden geladen...
              </Typography>
            </Box>
          )}
          
          {/* Render content only when not loading user data and not during login */}
          {!userDataLoading && !isLoginInProgress && <Outlet />}
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default MainLayout;